import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { useTags, TaggedContent } from "@/hooks/use-tags";
import { useLocation } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, FileText, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TagDisplayInline } from "@/components/tags/TagDisplay";
import { format } from "date-fns";
import { AppLayout } from "@/components/layout/AppLayout";

export default function TagPage({ params }: { params: { id: string } }) {
  const tagId = parseInt(params.id, 10);
  const [, navigate] = useLocation();
  const { tags, isLoading: tagsLoading, isFetched } = useTags();

  const currentTag = tags.find((tag) => tag.id === tagId);

  const { data: taggedContent, isLoading, error } = useQuery({
    queryKey: [`/api/tags/${tagId}/content`],
    queryFn: () => get<TaggedContent>(`/tags/${tagId}/content`),
    enabled: Number.isFinite(tagId),
  });

  if (tagsLoading || isLoading || !isFetched) {
    return (
      <AppLayout>
        <div className="flex justify-center items-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="container mx-auto py-8 text-center">
          <p className="text-destructive text-lg">Failed to load content</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/tags")}
          >
            Go Back
          </Button>
        </div>
      </AppLayout>
    );
  }

  if (!currentTag) {
    return (
      <AppLayout>
        <div className="container mx-auto py-8 text-center">
          <p className="text-muted-foreground">Tag not found.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/tags")}
          >
            Back to Tags
          </Button>
        </div>
      </AppLayout>
    );
  }

  const documents = taggedContent?.documents || [];

  return (
    <AppLayout>
      <div className="container mx-auto py-8 max-w-4xl">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate("/tags")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tags
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center">
            <span
              className="w-4 h-4 rounded-full mr-2 inline-block"
              style={{ backgroundColor: currentTag.color || "#94a3b8" }}
            />
            #{currentTag.name}
          </h1>
          {currentTag.description && (
            <p className="text-muted-foreground mt-2">{currentTag.description}</p>
          )}
        </div>

        <Tabs defaultValue="documents" className="mt-6">
          <TabsList className="mb-6">
            <TabsTrigger value="documents" className="flex items-center">
              <FileText className="h-4 w-4 mr-2" />
              Documents ({documents.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="documents">
            {documents.length === 0 ? (
              <div className="rounded-xl border border-dashed px-6 py-12 text-center text-muted-foreground">
                No documents tagged with #{currentTag.name} yet.
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <Card
                    key={doc.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => navigate(`/documents/${doc.id}/edit`)}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">
                        {doc.title || "Untitled"}
                      </CardTitle>
                      <CardDescription>
                        {doc.doc_type || "Document"}
                        {doc.updated_at
                          ? ` · ${format(new Date(doc.updated_at), "MMM d, yyyy")}`
                          : ""}
                      </CardDescription>
                    </CardHeader>
                    {doc.content && (
                      <CardContent>
                        <TagDisplayInline content={doc.content} maxLength={160} />
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
